import { z } from "zod";

/**
 * The SDK opened a fresh conversation inside the same runner, rotating the
 * session id. **Neither field carries the new id** — the live one arrives on
 * the `stream-start` that follows; `ConversationResetSchema` on the runner hop
 * carries why.
 *
 * Nothing renders it today. It travels so the host can stop a rotated-away id
 * being resumed against, and so a renderer that wants the boundary later has it
 * on the wire rather than needing a new one. Both ids are optional for the
 * reason `ConversationResetSchema` gives: the rotation is the fact, and a
 * missing diagnostic must not be able to refuse the payload carrying it.
 */
export const ConversationResetStreamEventSchema = z.object({
  type: z.literal("conversation-reset"),
  newConversationId: z.string().min(1).optional(),
  previousSessionId: z.string().min(1).optional()
});

/** The type the session's live phase is emitted as. */
export const SESSION_PHASE_STREAM_TYPE = "session-phase";

/**
 * What the SDK session is doing right now, field-for-field from the runner's
 * `session_phase` IPC event — `requesting` while a model call is in flight,
 * `compacting` while the context window is being compacted, `idle` once neither
 * holds.
 *
 * `idle` is a value the SDK sends rather than the absence of one, which is why
 * the member has no nullable phase: a reader answers three values and never has
 * to decide what a missing one meant.
 *
 * One frame per change, and the sequence is the point — a phase delivered once
 * would report that the session started doing something and never that it
 * stopped. `requesting` fires once per model API request, so this is the
 * chattiest member of this union by some margin.
 *
 * Nothing renders it today, and that is its resting state rather than an
 * unfinished slice: it travels so a reader that wants to draw a compaction
 * banner or a request spinner has the fact on the wire instead of needing a new
 * one. `compactError` is the exception worth naming — a compaction that failed
 * reports its reason on this member or nowhere, because no other SDK message
 * declares it.
 */
export const SessionPhaseStreamEventSchema = z.object({
  type: z.literal(SESSION_PHASE_STREAM_TYPE),
  phase: z.enum(["requesting", "compacting", "idle"]),
  compactResult: z.enum(["success", "failed"]).optional(),
  compactError: z.string().min(1).optional()
});

/** The type the SDK's own credential-state report is emitted as. */
export const AUTH_STATUS_STREAM_TYPE = "auth-status";

/**
 * The SDK reporting on its own credential state, field-for-field from the
 * runner's `auth_status` IPC event — whether an authentication exchange is under
 * way, the lines it produced, and the error it failed with.
 *
 * It travels because the failure it names is otherwise invisible: a consumer
 * routing on `type === 'system'` never sees this SDK message, so a credential
 * problem inside the sandbox surfaces as a session that simply stops answering.
 *
 * `output` and `error` arrive already scrubbed of secrets — the translator does
 * it, because this event is written into the conversation log and replays from
 * there. `AuthStatusSchema` on the runner hop carries the rest: why the lines
 * travel verbatim rather than concatenated, why `error` is independent of
 * `isAuthenticating`, and the reachability this was wired on.
 *
 * Nothing draws it today, which is a resting state rather than an
 * unfinished slice — it travels so a reader wanting to
 * show a credential banner has the fact on the wire instead of needing a new one.
 */
export const AuthStatusStreamEventSchema = z.object({
  type: z.literal(AUTH_STATUS_STREAM_TYPE),
  isAuthenticating: z.boolean(),
  output: z.array(z.string()),
  error: z.string().min(1).optional()
});

/** The type the SDK's own rate-limit reading is emitted as. */
export const RATE_LIMIT_STREAM_TYPE = "rate-limit";

/**
 * Where the session stands against its rate-limit window, field-for-field from
 * the runner's `rate_limit` IPC event — the status, the window, how much of it
 * is spent, and whether the session is drawing on overage.
 *
 * Only `status` is required, because only `status` is required on the SDK's own
 * type. `isUsingOverage` and `overageInUse` both travel because the vendor
 * declares both and gives no way to tell which is authoritative.
 *
 * Nothing draws it today, which is a resting state rather than a
 * gap. `RateLimitSchema` on the runner hop carries the rest — including
 * the reachability fork this member is typed against, and what overturning it
 * would cost.
 */
export const RateLimitStreamEventSchema = z.object({
  type: z.literal(RATE_LIMIT_STREAM_TYPE),
  status: z.enum(["allowed", "allowed_warning", "rejected"]),
  resetsAt: z.number().optional(),
  rateLimitType: z
    .enum([
      "five_hour",
      "seven_day",
      "seven_day_opus",
      "seven_day_sonnet",
      "seven_day_overage_included",
      "overage"
    ])
    .optional(),
  utilization: z.number().optional(),
  overageStatus: z.enum(["allowed", "allowed_warning", "rejected"]).optional(),
  overageResetsAt: z.number().optional(),
  overageDisabledReason: z
    .enum([
      "overage_not_provisioned",
      "org_level_disabled",
      "org_level_disabled_until",
      "out_of_credits",
      "seat_tier_level_disabled",
      "member_level_disabled",
      "seat_tier_zero_credit_limit",
      "group_zero_credit_limit",
      "member_zero_credit_limit",
      "org_service_level_disabled",
      "no_limits_configured",
      "fetch_error",
      "unknown"
    ])
    .optional(),
  isUsingOverage: z.boolean().optional(),
  overageInUse: z.boolean().optional(),
  surpassedThreshold: z.number().optional(),
  errorCode: z.literal("credits_required").optional(),
  canUserPurchaseCredits: z.boolean().optional(),
  hasChargeableSavedPaymentMethod: z.boolean().optional()
});

/** The type a loop-side text notification is emitted as. */
export const LOOP_NOTIFICATION_STREAM_TYPE = "loop-notification";

/**
 * A text notification the loop raised about itself, field-for-field from the
 * runner's `loop_notification` IPC event — the SDK's REPL notification queue,
 * which reaches a headless session too.
 *
 * Nothing draws it today, which is a resting state rather than an
 * unfinished slice — it travels so a reader wanting to
 * show a loop-side toast has the fact on the wire instead of needing a new one.
 * `LoopNotificationSchema` on the runner hop carries why this is a separate kind
 * from the banner below, what persists and what does not, and the reachability
 * this was wired on.
 *
 * `key` and `text` arrive already scrubbed of secrets — the translator does it,
 * because this event is written into the conversation log and replays from
 * there. `color` is sandbox-authored and unbounded; the runner-hop member
 * carries the caveat a renderer owes it.
 */
export const LoopNotificationStreamEventSchema = z.object({
  type: z.literal(LOOP_NOTIFICATION_STREAM_TYPE),
  key: z.string().min(1),
  text: z.string().min(1),
  priority: z.enum(["low", "medium", "high", "immediate"]),
  color: z.string().min(1).optional(),
  timeoutMs: z.number().nonnegative().optional()
});

/** The type a loop-side plain-text banner is emitted as. */
export const LOOP_BANNER_STREAM_TYPE = "loop-banner";

/**
 * A plain-text banner the loop emitted, field-for-field from the runner's
 * `loop_banner` IPC event — status lines, slash-command output, and a hook's
 * `systemMessage`.
 *
 * `content` carries the hook-event prefix the SDK writes (`"Stop says: …"`)
 * verbatim, already scrubbed of secrets by the translator — this event is
 * written into the conversation log and replays from there, so a scrub at the
 * wire's edge alone would not reach it. `preventContinuation` is what the vendor
 * declares as the statement of why a run stopped when a hook denied
 * continuation, and the runner-hop member records that the corpus has never
 * observed it populate. Nothing draws either today, which is the resting state
 * and not a gap.
 */
export const LoopBannerStreamEventSchema = z.object({
  type: z.literal(LOOP_BANNER_STREAM_TYPE),
  content: z.string().min(1),
  level: z.enum(["info", "notice", "suggestion", "warning"]),
  toolUseId: z.string().min(1).optional(),
  preventContinuation: z.boolean().optional()
});

/** The type a single auto-denied tool call is emitted as. */
export const PERMISSION_DENIED_STREAM_TYPE = "permission-denied";

/**
 * One tool call was auto-denied, field-for-field from the runner's
 * `permission_denied` IPC event apart from redaction — a deny rule, `dontAsk`
 * mode, a headless-agent auto-deny, or the auto-mode classifier.
 *
 * **Not the turn's denial record.** That lives on `message-end`, sourced from
 * the SDK's authoritative `permission_denials[]`, and stays the only complete
 * answer to "what did this turn refuse" — this member is per-call and misses at
 * least four denial paths by the vendor's own account. What it adds is the
 * reason and the rejection text, which the record does not carry.
 * `PermissionDeniedSchema` on the runner hop carries the excluded paths, the
 * measured negative this was wired on, and the one case where `toolUseId` names
 * no rendered row.
 *
 * `decisionReason` arrives already scrubbed of secrets — the translator does it,
 * because this event is written into the conversation log and replays from
 * there. The identifier fields are deliberately not scrubbed: a reader joins or
 * switches on each of them, and a redaction would break that with nothing going
 * red. The rejection prose is not carried at all — `tool-output-available`
 * already delivers it under this same `toolUseId`. The runner-hop member
 * carries both rules.
 *
 * Nothing draws it today, which is a resting state rather than an
 * unfinished slice.
 */
export const PermissionDeniedStreamEventSchema = z.object({
  type: z.literal(PERMISSION_DENIED_STREAM_TYPE),
  toolUseId: z.string().min(1),
  /** Blank-tolerant for the reason the runner-hop member gives at this field. */
  toolName: z.string(),
  taskId: z.string().min(1).optional(),
  decisionReasonType: z.string().min(1).optional(),
  decisionReason: z.string().min(1).optional()
});

/** The type the host worker's graceful-teardown notice is emitted as. */
export const WORKER_SHUTTING_DOWN_STREAM_TYPE = "worker-shutting-down";

/**
 * The host worker announced a graceful teardown and said why, field-for-field
 * from the runner's `worker_shutting_down` IPC event apart from redaction.
 *
 * **⚠️ Live-tail only.** The SDK stores these in the durable per-session event
 * stream, so a resumed session replays historical instances mid-stream — a
 * reader that treats one as "the worker is gone" will report a teardown that
 * happened days ago on a session that is working. Honour it only when no
 * further activity follows. `WorkerShuttingDownSchema` on the runner hop
 * carries that contract in full, the measured negative this was wired on, and
 * why the runner's own lifecycle deliberately does not read it.
 *
 * Nothing draws it today, and a slice that draws one owes the hazard above an
 * answer before it does.
 */
export const WorkerShuttingDownStreamEventSchema = z.object({
  type: z.literal(WORKER_SHUTTING_DOWN_STREAM_TYPE),
  reason: z.string().min(1)
});

/** The type an in-flight control request's progress frame is emitted as. */
export const CONTROL_REQUEST_PROGRESS_STREAM_TYPE = "control-request-progress";

/**
 * A control request the runner made is still in flight, field-for-field from
 * the runner's `control_request_progress` IPC event — accepted (`started`), or
 * retrying against the API (`api_retry`).
 *
 * **⚠️ `requestId` joins to nothing here.** The exchange it names begins and
 * ends inside the sandbox, so no reader on this hop holds the value or can
 * resolve it; it is carried only so two frames about one request group with
 * each other. `ControlRequestProgressSchema` on the runner hop carries why, and
 * the measured negative this was wired on.
 *
 * The four counters are present only under `api_retry` — the runner drops them
 * on a `started` frame rather than sending zeros, so absence means the SDK
 * reported none. Nothing draws any of it today.
 */
export const ControlRequestProgressStreamEventSchema = z.object({
  type: z.literal(CONTROL_REQUEST_PROGRESS_STREAM_TYPE),
  requestId: z.string().min(1),
  status: z.enum(["started", "api_retry"]),
  attempt: z.number().int().nonnegative().optional(),
  maxRetries: z.number().int().nonnegative().optional(),
  retryDelayMs: z.number().nonnegative().optional(),
  errorStatus: z.number().int().optional()
});

/** The type a still-running tool call's liveness frame is emitted as. */
export const TOOL_PROGRESS_STREAM_TYPE = "tool-progress";

/**
 * A tool call that is still running, field-for-field from the runner's
 * `tool_progress` IPC event — the SDK's 30-second heartbeat for a foreground
 * call. `ToolProgressSchema` on the runner hop carries why the id is resolved
 * rather than forwarded, the one case where it names no rendered row, and why
 * nothing persists it.
 *
 * Nothing draws it today, which is a resting state rather than an
 * unfinished slice — it travels so a reader wanting to
 * age a pending tool card has the fact on the wire instead of needing a new one.
 */
export const ToolProgressStreamEventSchema = z.object({
  type: z.literal(TOOL_PROGRESS_STREAM_TYPE),
  toolUseId: z.string().min(1),
  /** Blank-tolerant for the reason the runner-hop member gives at this field. */
  toolName: z.string(),
  elapsedSeconds: z.number().nonnegative(),
  heartbeat: z.boolean()
});

/** The type a finished tool call's caption is emitted as. */
export const TOOL_USE_SUMMARY_STREAM_TYPE = "tool-use-summary";

/**
 * A short natural-language caption for a tool call that has just run,
 * field-for-field from the runner's `tool_use_summary` IPC event.
 * `ToolUseSummarySchema` on the runner hop carries why it earns a kind of its
 * own, the environment gate without which it never fires, and why nothing
 * persists it.
 *
 * Nothing draws it today, which is a resting state rather than an
 * unfinished slice — it travels so a surface wanting a
 * per-call label has the fact on the wire instead of needing a new one.
 *
 * ⚠️ `summary` is sandbox-authored prose whose author is not established. A
 * renderer must not attribute it to the model, and must not bind it into a
 * context that trusts its length.
 */
export const ToolUseSummaryStreamEventSchema = z.object({
  type: z.literal(TOOL_USE_SUMMARY_STREAM_TYPE),
  summary: z.string().min(1),
  /** The calls this caption is about, by the id the reader already holds. */
  toolUseIds: z.array(z.string().min(1)).min(1)
});
